import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package, LogOut, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === null) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center pt-16"><div className="h-10 w-10 animate-spin rounded-full border-4 border-sand border-t-leaf" /></div>;
  }

  if (!user) {
    return <AuthCard />;
  }

  return <Profile user={user} onLogout={async () => { await logout(); navigate("/"); }} />;
}

function AuthCard() {
  const { login, register, google } = useAuth();
  const [mode, setMode] = useState("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const d = Object.fromEntries(new FormData(e.currentTarget).entries());
        await login(d.email, d.password);
      } else {
        const d = Object.fromEntries(new FormData(e.currentTarget).entries());
        await register(d.name, d.email, d.password);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 pb-24 pt-32" data-testid="account-auth-card">
      <Reveal>
        <div className="text-center">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Your pantry, your rules</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-forest">WELCOME IN.</h1>
          <p className="mt-2 text-sm text-ink/60">Track orders, one-tap checkout, zero repeat typing.</p>
        </div>

        <div className="mt-8 rounded-3xl border border-sand bg-white p-6 sm:p-8">
          <div className="grid grid-cols-2 gap-2 rounded-full bg-cream p-1.5">
            {[["login", "Log in"], ["register", "Sign up"]].map(([id, label]) => (
              <button
                key={id}
                data-testid={`account-tab-${id}`}
                onClick={() => { setMode(id); setError(""); }}
                className={`rounded-full py-2.5 text-xs font-extrabold uppercase tracking-wider transition-all ${
                  mode === id ? "bg-forest text-cream" : "text-forest/60 hover:text-forest"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form key={mode} onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">Name</span>
                <input data-testid="account-field-name" name="name" required placeholder="Aarav Sharma" className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
              </label>
            )}
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">Email</span>
              <input data-testid="account-field-email" name="email" type="email" required placeholder="you@example.com" className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">Password</span>
              <input data-testid="account-field-password" name="password" type="password" required minLength={8} placeholder={mode === "register" ? "Min 8 characters" : "••••••••"} className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
            </label>
            {mode === "login" && (
              <Link to="/forgot-password" data-testid="account-forgot-link" className="block text-right text-[11px] font-bold uppercase tracking-wider text-leaf hover:underline">
                Forgot password?
              </Link>
            )}
            {error && <p data-testid="account-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-xs font-bold text-red-600">{error}</p>}
            <button data-testid="account-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-leaf py-3.5 text-sm font-extrabold uppercase tracking-wide text-white transition-all hover:bg-forest active:scale-[0.98] disabled:opacity-60">
              {loading ? "One sec..." : mode === "login" ? "Log in" : "Create account"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-wider text-ink/40">
            <span className="h-px flex-1 bg-sand" /> or <span className="h-px flex-1 bg-sand" />
          </div>
          <button
            data-testid="account-google-button"
            onClick={google}
            className="flex w-full items-center justify-center gap-3 rounded-full border-2 border-sand bg-white py-3.5 text-sm font-extrabold text-forest transition-all hover:border-leaf hover:text-leaf active:scale-[0.98]"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52Z"/></svg>
            Continue with Google
          </button>
        </div>

        <Link to="/shop" data-testid="account-back-shop" className="mt-6 flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/50 hover:text-leaf">
          Back to the powders <ArrowRight className="h-3 w-3" />
        </Link>
      </Reveal>
    </div>
  );
}

function Profile({ user, onLogout }) {
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => (await api.get("/auth/orders")).data,
  });

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-32" data-testid="account-profile">
      <Reveal>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="h-14 w-14 rounded-full border-2 border-leaf object-cover" />
            ) : (
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-forest font-display text-xl font-extrabold text-cream">{(user.name || user.email)[0].toUpperCase()}</span>
            )}
            <div>
              <h1 className="font-display text-2xl font-extrabold text-forest">{user.name || "Pantry Boss"}</h1>
              <p className="text-sm text-ink/60">{user.email}</p>
            </div>
          </div>
          <button data-testid="account-logout" onClick={onLogout} className="flex items-center gap-2 rounded-full border border-sand bg-white px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide text-forest hover:border-red-300 hover:text-red-500">
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.08}>
        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-extrabold text-forest">
          <Package className="h-5 w-5 text-leaf" /> Your orders
        </h2>
      </Reveal>

      {isLoading ? (
        <div className="mt-6 space-y-3">{[1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-sand/50" />)}</div>
      ) : orders.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-dashed border-sand bg-white p-10 text-center">
          <Sparkles className="mx-auto h-6 w-6 text-leaf" />
          <p className="mt-3 font-display text-lg font-extrabold text-forest">No orders yet.</p>
          <p className="mt-1 text-sm text-ink/60">Your first pouch is one tap away.</p>
          <Link to="/shop" className="mt-5 inline-block rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream hover:bg-leaf">Shop the powders</Link>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {orders.map((o) => (
            <li key={o.order_id} data-testid={`account-order-${o.order_id}`}>
              <Link to={`/order/${o.order_id}`} className="flex items-center gap-4 rounded-2xl border border-sand bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-forest/10">
                <div className="flex gap-1.5">
                  {o.items.slice(0, 3).map((i) => <img key={i.slug} src={i.image} alt={i.name} className="h-12 w-12 rounded-lg object-cover" />)}
                </div>
                <div className="flex-1">
                  <p className="font-mono text-xs font-bold text-forest">{o.order_id}</p>
                  <p className="text-[11px] text-ink/50">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.items.reduce((s, i) => s + i.qty, 0)} item(s)</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-base font-extrabold text-forest">{inr(o.total)}</p>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider ${o.status === "paid" ? "bg-leaf/15 text-leaf" : "bg-forest/10 text-forest"}`}>{o.status.replace("_", " ")}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
